import {
  Avatar,
  Box,
  Card,
  Chip,
  Divider,
  Rating,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  getYear,
  REACTION_OPTIONS,
  getContentOptions,
  getStatusOptions,
  formatCalendarDate,
  utcDayKey,
} from "../../utils/utils";
import { useTheme } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import { StyledLink } from "../../components/CustomMUI";
import PosterTile from "../../components/PosterTile";
import { ExpandableTypography } from "../../components/ExpandableTypography";

export default function Activity({ item, onPosterClick }) {
  const {
    content_id,
    content_type,
    content_title,
    content_date,
    image_url,
    notes,
    rating,
    reactions,
    user_given_name,
    username,
    avatar_url,
    entry_status,
    date,
    activity_timestamp,
    published_at,
  } = item;
  const theme = useTheme();
  const isBelowSm = useMediaQuery(theme.breakpoints.down("sm"));
  const contentOptions = getContentOptions();
  const statusOptions = getStatusOptions(theme);
  const status = statusOptions[entry_status];

  // Two beats: the experience date (user-settable `date`) and the log date (when the
  // entry surfaced in the feed). When there's no experience date we show "logged on
  // <log date>"; when there is, we show "<status> on <date>" and only append
  // ", logged on <log date>" if the two fall on different days.
  const statusLabel = (status?.label ?? entry_status)?.toLowerCase();
  const verb = date ? statusLabel : "logged";
  // activity_timestamp is the feed-ordering value; fall back to published_at for
  // resilience against pre-change cached responses that lack it.
  const logTimestamp = activity_timestamp || published_at;
  const primaryDate = date || logTimestamp;
  const showLoggedOn =
    date && logTimestamp && utcDayKey(date) !== utcDayKey(logTimestamp);

  return (
    <Card
      sx={{
        width: "100%",
        py: 1.5,
        display: "flex",
        backgroundImage: "none",
        flexDirection: { xs: "column", sm: "row" },
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "33%",
            maxWidth: { xs: "80px", sm: "120px" },
            flexBasis: "30%",
            flexShrink: 0,
            borderRadius: 1,
          }}
        >
          <PosterTile
            onClick={onPosterClick}
            contentId={content_id}
            contentType={content_type}
            contentTitle={content_title}
            imageUrl={image_url}
            showStatus={isBelowSm ? "short" : "full"}
            readOnly={true}
            inline
          />
        </Box>
        <Box
          sx={{
            pl: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexBasis: "70%",
            flexGrow: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                verticalAlign: "middle",
                lineHeight: 0,
                mr: 1,
                // size the icon to match the text + nudge so its optical
                // centre lines up with the cap-height middle of the first line
                "& .MuiSvgIcon-root": {
                  fontSize: "1.3em",
                  transform: "translateY(-0.05em)",
                },
              }}
            >
              {contentOptions[content_type]?.icon}
            </Box>
            <StyledLink onClick={onPosterClick} sx={{ cursor: "pointer" }}>
              {content_title}
            </StyledLink>
            <Typography
              component="span"
              color="text.secondary"
              sx={{ fontStyle: "italic", ml: 1 }}
            >
              {" "}
              {content_type === "book" ? content_date : getYear(content_date)}
            </Typography>{" "}
          </Typography>{" "}
          <Divider />
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ textWrap: "balance" }}
          >
            <Box
              component={RouterLink}
              to={`/u/${username}`}
              sx={{
                display: "inline-flex",
                verticalAlign: "middle",
                lineHeight: 0,
                mr: 1,
                textDecoration: "none",
              }}
            >
              <Avatar
                src={avatar_url}
                imgProps={{ referrerPolicy: "no-referrer" }}
                alt={user_given_name}
                sx={{ width: 22, height: 22 }}
              />
            </Box>
            <StyledLink
              component={RouterLink}
              to={`/u/${username}`}
              variant="body1"
            >
              @{username}
            </StyledLink>{" "}
            {/* Verb + matching status icon, kept together so the icon never
                wraps onto a line by itself while the pair still flows inline. */}
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              {verb}
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  verticalAlign: "middle",
                  lineHeight: 0,
                  ml: 0.5,
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.2em",
                    transform: "translateY(-0.05em)",
                  },
                }}
              >
                {status?.icon}
              </Box>
            </Box>{" "}
            on {formatCalendarDate(primaryDate, "dropYear")}
            {showLoggedOn &&
              `, logged ${formatCalendarDate(logTimestamp, "dropYear")}`}
          </Typography>
          {rating && <Rating value={rating} readOnly precision={0.5} />}
          {reactions && (
            <Box sx={{ flexWrap: "wrap" }}>
              {reactions.map((reaction, index) => {
                const option = REACTION_OPTIONS.find(
                  (option) => option.label === reaction,
                );
                return (
                  <Chip
                    key={index}
                    label={reaction}
                    icon={
                      option?.icon || (
                        <span role="img" aria-label={option?.label}>
                          {option?.emoji}
                        </span>
                      )
                    }
                    size="small"
                    sx={{
                      "&.MuiChip-root": {
                        mb: 0.5,
                        mt: 0.5,
                        mr: 1,
                      },
                    }}
                  />
                );
              })}
            </Box>
          )}
          {notes && (
            <ExpandableTypography
              text={notes}
              lineClamp={5}
              variant="body1"
              whiteSpace="pre-wrap"
              blendColor={false}
            />
          )}
        </Box>
      </Box>
    </Card>
  );
}
